/* eslint-disable max-len */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, StrikeZone, Table, BaseRunners, HitLocation, dateTranslate, number, ordinal, styles, Dropdown } from 'yankees-react-utils';
import { useOktaQueries, Player, MLBAMLiveFeed, StrikeZones } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import { VideoPlayerModal } from '../../../components/VideoPlayer';
import VideoPlaylistButton from '../../../components/VideoPlaylistButton';
import PlayerAuthoredReportNote from '../../../components/Notes/PlayerAuthoredReportNote';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import BPCard from '../Hitter/BPCard';
import { bcbefore, buildPlaylist } from './mlbamMapping';

const StyledSummaryTableWrapper = styled.div`
    &&& {
        font-size: 120%;
        table tr th.ab--videobtn { width: 1%; }
        table tr th.ab--abno { width: 1%; }
        table tr th.ab--inning { width: 1%; }
        table tr th.ab--situation { width: 5%; }
        table tr th.ab--runners { width: 5%; }
        table tr th.ab--pitch { width: 10%; }
        table tr th.ab--hittype { width: 10%; }
        table tr th.ab--result { width: 10%; }
        table tr th.ab--hitloc { width: 10%; }
        table tr th.ab--notes { width: 40%; }
    }
`;

const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;
`;

const buildQueries = (playerid, gameid, gameinfo) => {
    const { yearid, levelid, mlbamgamepk } = gameinfo;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['feed', mlbamgamepk],
            queryFn: (t) => MLBAMLiveFeed.gameFeed(t, mlbamgamepk, { hydrate: 'alignment,preState,strikeZone' }),
        },
        {
            queryKey: ['guids', mlbamgamepk],
            queryFn: (t) => MLBAMLiveFeed.statcastPlays(t, mlbamgamepk, { hydrate: 'analytics(video,pitch,hit,metrics,metaData)' }),
        },
        {
            queryKey: ['strikeZoneContoursLhb', playerid, gameid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: true, yearid, levelid }),
        },
        {
            queryKey: ['strikeZoneContoursRhb', playerid, gameid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: false, yearid, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response) => {
    const player = response[0].data || {};
    const statsApiFeed = response[1].data || {};
    const statcastGuids = response[2].data || {};
    const contoursLhb = response[3].data || {};
    const contoursRhb = response[4].data || {};
    return { player, statsApiFeed, statcastGuids, contoursLhb, contoursRhb };
};

const getPitchLoc = (pitchData) => {
    if (!pitchData?.coordinates) return {};
    let pX = pitchData.strikeZoneInfo?.plateX ?? null;
    let pZ = pitchData.strikeZoneInfo?.plateZ ?? null;
    if (pX === null || pZ === null || (pitchData.strikeZoneInfo?.plateY - (8.5 / 12) > (1 / 13))) {
        console.log('pitch', pitchData);
        const { x0, y0, z0, vX0, vY0, vZ0, aX, aY, aZ } = pitchData.coordinates;
        const time50HP = ((-1 * vY0) - Math.sqrt((vY0 ** 2) - (2 * aY * (y0 - (8.5 / 12.0))))) / aY; // Time from 50ft to ABS zone plane
        const pls = (0.5 * aX * (time50HP ** 2)) + (vX0 * time50HP) + x0; // horizontal location
        pZ = (0.5 * aZ * (time50HP ** 2)) + (vZ0 * time50HP) + z0; // vertical location
        pX = pls;
    }
    return { x: -1 * pX, y: pZ };
};

const toStrikeZoneSeries = (translate, pitches) => pitches.map((p) => (
    {
        name: `&nbsp;<b>${translate('data.pitch')} #${p.pitchNumber}</b>`,
        index: p.pitchNumber,
        id: `${p.playId}`,
        visible: true,
        marker: {
            symbol: (p.details.code === 'B' || p.details.code === 'C') ? 'circle' : 'diamond', // take == circle
            radius: 5,
            fillColor: '#808080',
        },
        data: [{
            // x: p.pitchData.coordinates.pX * -1,
            // y: p.pitchData.coordinates.pZ,
            ...getPitchLoc(p.pitchData),
            tooltip: `&nbsp;${p.preCount?.balls}-${p.preCount?.strikes}, ${p.preCount?.outs} ${translate('baseball.outs', { count: p.preCount?.outs })}<br/>
                    ${p.details?.type?.description}${p.pitchData?.startSpeed ? `, ${translate('format.abbr-mph', { value: number(p.pitchData.startSpeed, 1) })}` : ''}<br/>
                    ${translate('common.result')}: ${p.details?.description}`,
            statsapiPitch: p,
            playid: p.playId,
            dataLabels: {
                enabled: true,
                x: 1,
                formatter: () => p.pitchNumber,
                color: '#000',
                borderColor: '#FFF',
            },
        }],
    }
));

const getBatters = (apiFeed, nyyTeamId) => {
    const team = apiFeed?.liveData?.boxscore?.teams[apiFeed.liveData.boxscore.teams.away.team.id === nyyTeamId ? 'away' : 'home'];
    const battersInGame = Array.from(new Set(apiFeed?.liveData?.plays?.allPlays?.map((p) => p.matchup.batter.id)));

    if (!team) return [];

    return Object.values(team.players)
        .filter((p) => battersInGame.includes(p.person.id))
        .sort((a, b) => a.person.fullName.split(' ').at(-1).localeCompare(b.person.fullName.split(' ').at(-1)))
        .map((p) => ({ name: p.person.fullName, value: p.person.id }));
};

const getNyyTeamId = (gameInfo, showPlayerDropdown) => {
    if (!showPlayerDropdown) return null;

    if ([8, 20, 21].includes(gameInfo.levelid)) return gameInfo.hometeamorg === 31 ? gameInfo.hometeamid : gameInfo.awayteamid;

    return null;
};

const MlbamGameSummary = ({ playerid, gameid, gameinfo }) => {
    const { t: translate } = useTranslation();
    const videoPlayerModalContainer = useRef();
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoPlaylist, setVideoPlaylist] = useState(null);
    const userPlayer = usePlayerSelector();
    const [selectedPlayerId, setSelectedPlayerId] = useState(playerid);
    const dataQueries = buildQueries(selectedPlayerId, gameid, gameinfo);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { player, statsApiFeed, statcastGuids, contoursLhb, contoursRhb } = mapDataResponse(dataQueriesResponse);
    // shortcut: map ABs situation onto each pitch of AB
    // eslint-disable-next-line no-unused-expressions
    statsApiFeed?.liveData.plays?.allPlays?.forEach((play) => {
        play.playEvents.forEach((pitch) => {
            pitch.about = { ...play.about };
            pitch.matchup = { ...play.matchup };
        });
    });

    const showPlayerDropdown = userPlayer?.IsMlb && [8, 20, 21, 29].includes(gameinfo.levelid) && (gameinfo.hometeamorg === 31 || gameinfo.awayteamorg === 31);
    const nyyTeamId = getNyyTeamId(gameinfo, showPlayerDropdown);
    const gameBatters = getBatters(statsApiFeed, nyyTeamId);
    const isSelectedPlayerLoggedInUser = playerid === selectedPlayerId;

    // was player a batter or pitcher in this game?
    const showBatter = statsApiFeed?.liveData.plays?.allPlays?.some((p) => p.matchup?.batter.id === player.PlayerIDMapping.MLBAMPlayerID);
    const showPitcher = statsApiFeed?.liveData.plays?.allPlays?.some((p) => p.matchup?.pitcher.id === player.PlayerIDMapping.MLBAMPlayerID);

    const batterAbs = statsApiFeed?.liveData?.plays?.allPlays?.filter((p) => p.matchup?.batter.id === player.PlayerIDMapping.MLBAMPlayerID) ?? [];
    const opposingPitchers = batterAbs
        .map((ab) => ({ pitcherid: ab.matchup.pitcher.id, name: ab.matchup.pitcher.fullName, throws: ab.matchup.pitchHand.code === 'R' ? 1 : 2 }))
        .reduce((p, c) => { if (!p.some((x) => x.pitcherid === c.pitcherid)) p.push(c); return p; }, []);

    const openPitchVideo = (pitch) => {
        if (!pitch?.statsapiPitch) return;
        setVideoPlaylist(buildPlaylist([pitch.statsapiPitch], statcastGuids, translate));
        setShowVideoModal(true);
    };

    return (
        <>
            {isSelectedPlayerLoggedInUser && <BPCard gameid={gameid} playerid={playerid} gamedate={gameinfo.gamedate} />}
            {!showBatter && !showPitcher && (
                <h3 className="mt-5 mb-5">{translate('sections.post-game.home.no-data-built')}</h3>
            )}

            {showBatter && (
                <StyledReportCard>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="text-start">
                            {translate('sections.post-game.hitter.at-bat-summary')}
                            <VideoPlaylistButton
                                className="ms-3"
                                playlist={buildPlaylist(batterAbs.flatMap((ab) => ab.playEvents?.filter((e) => e.isPitch)), statcastGuids, translate)}
                                color={styles.theme.background.astronaut}
                                icon={translate('common.all')}
                            />
                        </h2>
                        {showPlayerDropdown && (
                            <Dropdown
                                options={gameBatters}
                                value={{ name: gameBatters?.find((gb) => gb.value === selectedPlayerId)?.name }}
                                onSelect={(v) => setSelectedPlayerId(v.value)}
                            />
                        )}
                    </div>
                    <StyledSummaryTableWrapper>
                        <Table size="sm">
                            {opposingPitchers && opposingPitchers.map((pitcher) => {
                                const allPitches = batterAbs.filter((ab) => ab.matchup.pitcher.id === pitcher.pitcherid)
                                    .flatMap((ab) => ab.playEvents?.filter((e) => e.isPitch));
                                const pitcherPlaylist = buildPlaylist(allPitches, statcastGuids, translate);
                                return (
                                    <tbody key={pitcher.pitcherid}>
                                        <tr>
                                            <th className="table-title text-start" colSpan={99}>
                                                {pitcher.name} ({pitcher.throws === 1 ? translate('baseball.rhp') : translate('baseball.lhp')})
                                            </th>
                                        </tr>
                                        <tr>
                                            <th className="ab--videobtn">
                                                <VideoPlaylistButton
                                                    className="ms-1"
                                                    playlist={pitcherPlaylist}
                                                    color={styles.theme.background.astronaut}
                                                    icon={translate('common.all')}
                                                />
                                            </th>
                                            <th className="ab--abno">{translate('baseball.abbr-atbat')}</th>
                                            <th className="ab--inning">{translate('baseball.inning')}</th>
                                            <th className="ab--situation">{translate('baseball.count')}/{translate('baseball.outs_other')}</th>
                                            <th className="ab--runners">{translate('baseball.runners_other')}</th>
                                            <th className="ab--pitch">{translate('baseball.pitches_one')}</th>
                                            <th className="ab--result">{translate('common.result')}</th>
                                            <th className="ab--hitloc" />
                                            {isSelectedPlayerLoggedInUser && <th className="ab--notes">{translate('components.notes.notes')}</th>}
                                        </tr>
                                        {batterAbs && batterAbs.filter((ab) => ab.matchup.pitcher.id === pitcher.pitcherid).map((ab, idx) => {
                                            const abPitches = [...ab.playEvents?.filter((e) => e.isPitch)];
                                            if (!abPitches.length) return <></>;

                                            const actionPitch = [...abPitches].pop() ?? {};
                                            const actionGuid = statcastGuids?.find((g) => g.guid === actionPitch.playId);
                                            const baseState = bcbefore(actionPitch);
                                            const abno = idx + 1;
                                            const abPlaylist = buildPlaylist(abPitches, statcastGuids, translate);

                                            let bipX = actionGuid?.hitSegment?.landingData?.position.x;
                                            let bipY = actionGuid?.hitSegment?.landingData?.position.y;
                                            const hitDist = parseInt(actionGuid?.calculatedMetrics?.find((m) => m.metricId === 1050)?.value ?? 0);
                                            const angle = actionGuid?.hitSegment?.launchData?.direction;
                                            if (actionPitch.hitData?.trajectory === 'ground_ball' && hitDist && angle !== null) {
                                                bipX = hitDist * Math.sin(angle * Math.PI / 180.0);
                                                bipY = hitDist * Math.cos(angle * Math.PI / 180.0);
                                            }
                                            const getAbsDescription = (atbat, pitch) => {
                                                if (!atbat?.reviewDetails?.player?.fullName || atbat.reviewDetails.reviewType !== 'MJ') {
                                                    return '';
                                                }

                                                const { reviewDetails } = atbat;
                                                const isOverturned = !!reviewDetails.isOverturned;

                                                const currentIsStrike = !!pitch?.details?.isStrike;

                                                const originalCall = isOverturned
                                                    ? (currentIsStrike ? 'ball' : 'strike')
                                                    : (currentIsStrike ? 'strike' : 'ball');

                                                const status = isOverturned ? 'Overturned' : 'Confirmed';

                                                return `${reviewDetails.player.fullName} challenged the ${originalCall} call. ${status}.`;
                                            };

                                            const absReviewDescription = getAbsDescription(ab, actionPitch);

                                            return (
                                                <tr key={actionPitch.playId} style={{ height: '1px' }}>
                                                    <td>
                                                        {abPlaylist.length > 0 && (
                                                            <VideoPlaylistButton
                                                                className="ms-1"
                                                                playlist={abPlaylist}
                                                                color={styles.theme.background.astronaut}
                                                                icon={translate('baseball.abbr-atbat')}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>{abno}</td>
                                                    <td>{ab.about?.inning}</td>
                                                    <td>{actionPitch.preCount?.balls}-{actionPitch.preCount?.strikes}, {actionPitch.preCount?.outs} {translate('baseball.outs', { count: actionPitch.preCount?.outs })}</td>
                                                    <td><BaseRunners baseState={baseState} height={20} width={30} emptyColor="#afafaf" /></td>
                                                    <td>
                                                        {actionPitch.details?.type?.description}
                                                        {actionPitch.pitchData?.startSpeed && `, ${translate('format.abbr-mph', { value: number(actionPitch.pitchData?.startSpeed) })}`}
                                                        <div style={{ zoom: '70%' }}>
                                                            <StrikeZone
                                                                size="xs"
                                                                showbatter={false}
                                                                showoffgrid
                                                                pitcherView={false}
                                                                seriesData={[{ marker: { radius: 3 }, data: [{ x: actionPitch.pitchData?.coordinates?.pX * -1, y: actionPitch.pitchData?.coordinates?.pZ }] }]}
                                                                sztop={userPlayer?.SzTop}
                                                                szbottom={userPlayer?.SzBottom}
                                                                useABSRulebookZone
                                                                useBaseballMarkers
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {[ab.result?.event].filter((x) => x).join(', ')}
                                                        {ab.about?.hasReview && ab.reviewDetails && ab.reviewDetails.reviewType === 'MJ' && (
                                                            <>
                                                                &nbsp;({ab.reviewDetails.isOverturned ? 'Overturned' : 'Confirmed'})
                                                                <div>{absReviewDescription}</div>
                                                            </>
                                                        )}
                                                        {actionPitch.hitData?.launchSpeed && (
                                                            <div>{translate('format.abbr-mph', { value: number(actionPitch.hitData?.launchSpeed) })}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {actionGuid?.hitSegment?.launchData?.speed && (
                                                            <div style={{ zoom: '60%' }}>
                                                                <HitLocation
                                                                    ballinplayx={bipX}
                                                                    ballinplayy={bipY}
                                                                    ishr={ab.result.eventType === 'home_run'}
                                                                />
                                                            </div>
                                                        )}
                                                    </td>
                                                    {abno === 1 && isSelectedPlayerLoggedInUser && (
                                                        <td
                                                            rowSpan={batterAbs.filter((xab) => xab.matchup.pitcher.id === pitcher.pitcherid).length}
                                                            style={{ backgroundColor: 'white', height: 'inherit' }}
                                                        >
                                                            <PlayerAuthoredReportNote
                                                                className="h-100 mt-0"
                                                                type="mynt"
                                                                section="2"
                                                                player={userPlayer}
                                                                pitcher={{ PlayerID: pitcher.pitcherid, FullName: pitcher.name }}
                                                                gameId={gameid}
                                                                compact
                                                                customTitleForModal={`${pitcher.name} ${dateTranslate(translate, gameinfo.gamedate)}`}
                                                                customPlaceholder={translate('components.notes.enter-game-notes')}
                                                                hideWhenNotFound
                                                            />
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                );
                            })}
                        </Table>

                        {isSelectedPlayerLoggedInUser && (
                            <PlayerAuthoredReportNote
                                type="mynt"
                                section="4"
                                compact
                                player={userPlayer}
                                gameId={gameid}
                                customTitleForModal={`${translate('components.notes.game-notes')} ${dateTranslate(translate, gameinfo.gamedate)}`}
                                customPlaceholder={translate('components.notes.enter-game-notes')}
                                hideWhenNotFound
                            />
                        )}
                    </StyledSummaryTableWrapper>
                </StyledReportCard>
            )}

            {showBatter && (
                <StyledReportCard>
                    <div className="sd-legend">
                        <span>
                            <svg><circle fill="transparent" stroke="#333333" cx="6" cy="6" r="5" strokeWidth="2" /></svg>
                            <h4>{translate('sections.post-game.hitter.take')}</h4>
                        </span>
                        <span>
                            <svg viewBox="-1 -1 12 12"><path fill="transparent" stroke="#333333" d="M 5 0 L 10 5 5 10 0 5 5 0 Z" strokeWidth="2" /></svg>
                            <h4>{translate('sections.post-game.hitter.swing')}</h4>
                        </span>
                    </div>
                    <div className="d-flex flex-wrap justify-content-left">
                        {batterAbs.map((ab) => {
                            const pitcher = opposingPitchers.find((p) => p.pitcherid === ab.matchup.pitcher.id);
                            const showlhb = player.Bats === 2 || (player.Bats === 3 && pitcher.throws === 1);
                            const strikeZoneContours = showlhb ? contoursLhb : contoursRhb;
                            const abPitches = [...ab.playEvents?.filter((e) => e.isPitch)];
                            if (!abPitches.length) return <></>;

                            const abPlaylist = buildPlaylist(abPitches, statcastGuids, translate);
                            const series = toStrikeZoneSeries(translate, abPitches);
                            return (
                                <ReportCard key={abPitches[0].playId} className="m-2">
                                    <h4>
                                        {pitcher.name} ({pitcher.throws === 1 ? translate('baseball.rhp') : translate('baseball.lhp')})
                                        {abPlaylist.length > 0 && (<VideoPlaylistButton className="ms-1" playlist={abPlaylist} />)}
                                    </h4>
                                    <h5>
                                        {ab.about?.isTopInning ? translate('baseball.abbr-top') : translate('baseball.abbr-bottom')} {ordinal(ab.about?.inning)}, {ab.result?.event}
                                    </h5>
                                    <StrikeZone
                                        enableZoom={false}
                                        contourPoints={pitcher.throws === 1 ? strikeZoneContours.rhp : strikeZoneContours.lhp}
                                        seriesData={series}
                                        size="lg"
                                        height={400}
                                        showlhb={showlhb}
                                        showrhb={!showlhb}
                                        yopposite={showlhb}
                                        showoffgrid
                                        pitcherView={false}
                                        pitchVideoFn={null}
                                        onClick={(pt) => openPitchVideo(pt)}
                                        sztop={userPlayer?.SzTop}
                                        szbottom={userPlayer?.SzBottom}
                                        useABSRulebookZone
                                        useBaseballMarkers
                                    />
                                </ReportCard>
                            );
                        })}
                    </div>
                </StyledReportCard>
            )}
            <div ref={videoPlayerModalContainer} />
            <VideoPlayerModal
                playlist={videoPlaylist}
                show={showVideoModal}
                onHide={() => { setVideoPlaylist(null); setShowVideoModal(false); }}
                container={videoPlayerModalContainer}
            />
        </>
    );
};

MlbamGameSummary.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    gameinfo: PropTypes.shape({
        gameid: PropTypes.number,
        gamedate: PropTypes.string,
        levelid: PropTypes.number,
        hometeamorg: PropTypes.number,
        hometeamid: PropTypes.number,
        awayteamorg: PropTypes.number,
        awayteamid: PropTypes.number,
    }).isRequired,
};

export default MlbamGameSummary;
